import { useEffect } from 'react';
import ms from 'ms';
import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { CHANNELS_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { useAuth } from '@context/auth/base';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  fetchGQL,
  RecommendedChannelsDocument,
  SetUserEngagementOnChannelDocument,
  useMutation,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useStableCallback } from '@hooks/useStableCallback';
import { useChannelsEnabled } from './helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation SetUserEngagementOnChannel($channelId: UUID!) {
    setUserEngagementOnChannel(channelId: $channelId) {
      __typename
      ... on Error {
        message
      }
    }
  }
`);

const DELAY = ms('5 seconds');

RefetchOnComplete({
  trigger: [SetUserEngagementOnChannelDocument],
  refetch: [RecommendedChannelsDocument],
  throttled: '2 seconds',
});

export function useChannelEngagement({ channelId }: { channelId: string | undefined }) {
  const { enabled: channelsEnabled } = useChannelsEnabled();

  const { authUser } = useAuth();
  const logError = useLogError();

  const { mutateAsync, isLoading } = useMutation(SetUserEngagementOnChannelDocument, {
    retry: 3,
  });

  const setUserEngagement = useStableCallback(async () => {
    if (!channelId || !authUser || !channelsEnabled) return;

    try {
      const result = await mutateAsync({ channelId });

      if (
        result.data.setUserEngagementOnChannel?.__typename !==
        'MutationSetUserEngagementOnChannelSuccess'
      ) {
        throw new Error(result.data.setUserEngagementOnChannel?.message);
      }
    } catch (error) {
      const message = 'Failed to set user engagement on channel';

      logError({
        action: CHANNELS_ERROR_ACTIONS.SET_CHANNEL_ENGAGEMENT_FAILED,
        error,
        errorType: 'CHANNELS',
        isArtistOnlyPage: false,
        level: 'error',
        message,
        pillar: 'CHANNELS',
        indexedTags: {
          channelId,
        },
      });
    }
  });

  /*
   * We need to call this mutation twice, once when the user enters the channel page,
   * and another when the user leaves the channel page
   */
  useEffect(() => {
    if (!authUser || !channelId || !channelsEnabled) return;

    const engagement = setTimeout(() => {
      setUserEngagement();
    }, DELAY);

    return () => {
      clearTimeout(engagement);
    };
  }, [authUser, channelId, channelsEnabled, setUserEngagement]);

  useEffect(() => {
    if (!authUser || !channelId || !channelsEnabled) return;

    const handleOnBeforeUnload = async () => {
      await fetchGQL(SetUserEngagementOnChannelDocument, {
        variables: { channelId },
        keepalive: true,
      });
      return;
    };

    window.addEventListener('beforeunload', handleOnBeforeUnload);
    Router.events.on('routeChangeStart', handleOnBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleOnBeforeUnload);
      Router.events.off('routeChangeStart', handleOnBeforeUnload);
    };
  }, [authUser, channelId, channelsEnabled, setUserEngagement]);

  return { setUserEngagement, isLoading };
}
